import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { apiFetch } from '../lib/api';
import { decodeJwtPayload } from '../lib/decodeJwtPayload';
import { TENANT_ACCESS_REVOKED_EVENT } from '../lib/tenantAccessRevoked';
import { LoginSupersededError } from '../lib/loginSuperseded';

export { LoginSupersededError };

interface User {
  id: string;
  email: string;
  name: string;
}

interface AuthResponse {
  token: string;
  user: User;
}

interface SmeRegisterResponse {
  userId: string;
  // ONB.B2-FIX.D2-FOLLOWON.A1 (#1110) — widened to optional. Invitation-flow
  // registrations return without a tenantId (no tenant is created); solo-flow
  // registrations still return a string. Backend OMITS the field entirely
  // in invitation flow (not `tenantId: null`) — see auth.ts:/auth/register
  // response construction.
  tenantId?: string;
  // #1975 — widened from the `true` literal. An invited user on the academic
  // or setup path whose invitation token validated server-side gets
  // `false` + a `token`: no verification email was sent, because consuming
  // their invitation token IS the proof of address control. Everyone else
  // still gets `true` and no token.
  requiresVerification: boolean;
  email: string;
  // #1975 — present ONLY when requiresVerification is false. A deliberately
  // TENANT-LESS session: it exists so the user can reach the accept endpoint
  // (which is authenticate-gated), and the accept response then re-issues a
  // tenant-scoped token. Do not treat this as a finished login.
  token?: string;
}

export interface RegisterParams {
  name: string;
  email: string;
  password: string;
  orgName?: string;
  orgSize?: string;
  tier?: string;
  /**
   * Optional same-origin redirect target (ONB.B2-FIX.D2). When the user
   * arrived at /register via a magic-link round-trip (e.g., /setup/accept
   * → /login?redirect=... → /register?redirect=...), this is the path to
   * return to after email verification. The backend strips unsafe values
   * server-side; the frontend also guards via isSafeRedirect before
   * passing into this call.
   */
  redirect?: string;
  /**
   * #1825 — pricing-page plan intent (individual/starter/growth/
   * platform-partner), read from /register?plan= and forwarded as-is.
   * The backend independently re-validates against its own allowlist
   * before persisting to users.intended_plan — this is not a trust
   * boundary the frontend guards alone.
   */
  plan?: string;
  /**
   * #1975 — raw invitation token + its path discriminator, forwarded so the
   * backend can decide whether this registration qualifies to skip email
   * verification.
   *
   * The frontend reads these off the invitation redirect purely as transport;
   * it is NOT a trust boundary. The backend hashes the token, requires it to
   * match a live (PENDING, unexpired) invitation, and requires the invited
   * address to equal the address being registered, before suppressing a
   * single email. An invalid or absent pair simply verifies as before.
   */
  invitationToken?: string;
  invitationType?: 'academic' | 'setup';
  /**
   * #2189 (SIGNUP.B1) — deliberately snake_case, not termsAccepted, matching
   * every other field in this interface. registerSme() JSON.stringify's this
   * object directly with no key remapping, and POST /auth/register's Zod
   * schema (auth.ts) names this field terms_accepted at the wire level (it
   * maps straight to the terms_accepted_at DB column, per SIGNUP.A1) — unlike
   * orgName/orgSize/invitationToken/invitationType above, which the backend
   * also spells camelCase. Do not "fix" the casing here without checking
   * auth.ts's schema first; renaming it silently reintroduces the exact bug
   * this field exists to fix (server 400s on a field it never receives).
   */
  terms_accepted: boolean;
  /**
   * SIGNUP.B1 (#2189) D1 — optional marketing consent, a SEPARATE field from
   * terms_accepted (NFR-06 / DPDP Act). Same snake_case wire-name rule as
   * terms_accepted above: registerSchema names it marketing_opt_in, and a
   * camelCase key here would be stripped server-side with a 201.
   */
  marketing_opt_in?: boolean;
  /**
   * SIGNUP.B1 (#2189) D2/D3 — optional B2B signup fields, snake_case at the
   * wire for the same reason as terms_accepted. Values are the migration 0214
   * slugs; the API drops anything else to NULL rather than rejecting.
   */
  signup_role?: string;
  signup_team_size?: string;
  signup_referral_source?: string;
}

interface AuthContextValue {
  user: User | null;
  token: string | null;
  /**
   * Resolves with the token this login installed (PR #3275 R2: callers tie
   * later resets to it). Rejects with LoginSupersededError, installing nothing,
   * if a newer session change started while it waited (PR #3275, Codex bot).
   */
  login: (email: string, password: string) => Promise<string>;
  logout: () => void;
  /**
   * #3269 — drop the IN-MEMORY session only (token/user state), and only if it
   * is still the session `expectedToken` names (compare-and-reset; PR #3275
   * R2). Used when the API refused that token with TENANT_ACCESS_REVOKED:
   *  - storage is never touched here: the handler in lib/tenantAccessRevoked.ts
   *    already cleared it when it still held the refused token, and a newer
   *    login another tab stored must survive (#3249) — which logout() would not
   *    guarantee;
   *  - a late refusal of an OLDER token must not reset a NEWER session already
   *    installed in this same provider (e.g. an abandoned login attempt's
   *    refusal arriving after the next login succeeded).
   */
  resetSession: (expectedToken: string | null) => void;
  /** Same supersede rule as login(). */
  register: (name: string, email: string, password: string) => Promise<void>;
  /** Store a pre-obtained token + user (OAuth callback flow) */
  setSession: (token: string, user: User) => void;
  registerSme: (params: RegisterParams) => Promise<SmeRegisterResponse>;
  setAuthFromToken: (token: string) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function loadStoredUser(): User | null {
  try {
    const raw = localStorage.getItem('blueprint_user');
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setTokenState] = useState<string | null>(() => localStorage.getItem('blueprint_token'));
  // PR #3275 R2 — the token this provider holds RIGHT NOW, readable from stable
  // callbacks (resetSession, the event listener) without re-binding them on
  // every token change. Every write goes through setToken below.
  const tokenRef = useRef<string | null>(token);
  const setToken = useCallback((next: string | null) => {
    tokenRef.current = next;
    setTokenState(next);
  }, []);
  const [user, setUser] = useState<User | null>(loadStoredUser);
  // PR #3275 (Codex bot) — session-intent generation. Bumped when any
  // USER-INITIATED session change starts: login()/register() at call time
  // (before the await), setSession(), setAuthFromToken(), logout(). login()
  // and register() write their response only if no newer change started while
  // they waited, so an abandoned attempt can't overwrite a newer session.
  // Deliberately NOT bumped by resetSession() or the tenant-access-revoked
  // listener: a background refusal of a stale token while a fresh login is
  // pending must not cancel that login.
  const sessionGenRef = useRef(0);

  // Functions wrapped in useCallback + value memoised so the context value
  // identity is stable across renders that don't actually change auth state.
  // Previously each AuthProvider render minted fresh function references,
  // which meant any consumer with `setSession` (or other functions) in a
  // useEffect dep array would re-run the effect on every parent re-render —
  // a real source of effect-double-fire bugs (see OAuthCallbackPage).
  const persist = useCallback((data: AuthResponse) => {
    localStorage.setItem('blueprint_token', data.token);
    localStorage.setItem('blueprint_user', JSON.stringify(data.user));
    setToken(data.token);
    setUser(data.user);
  }, [setToken]);

  const login = useCallback(async (email: string, password: string): Promise<string> => {
    const gen = ++sessionGenRef.current;
    const data = await apiFetch<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    if (gen !== sessionGenRef.current) throw new LoginSupersededError();
    persist(data);
    return data.token;
  }, [persist]);

  const register = useCallback(async (name: string, email: string, password: string) => {
    const gen = ++sessionGenRef.current;
    const data = await apiFetch<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ name, email, password }),
    });
    if (gen !== sessionGenRef.current) throw new LoginSupersededError();
    persist(data);
  }, [persist]);

  const setSession = useCallback((newToken: string, newUser: User) => {
    ++sessionGenRef.current;
    persist({ token: newToken, user: newUser });
  }, [persist]);

  const registerSme = useCallback(async (params: RegisterParams): Promise<SmeRegisterResponse> => {
    const data = await apiFetch<SmeRegisterResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return data;
  }, []);

  const setAuthFromToken = useCallback((jwt: string) => {
    const payload = decodeJwtPayload<{ userId: string; email?: string; name?: string }>(jwt);
    if (!payload) return; // Invalid token — ignore (same no-op as before)
    // R2 (#1975) — preserve any identity we already hold for THIS user.
    // The JWT carries only `userId`, so building the User purely from it
    // blanked name/email. That was tolerable when this ran once at the end of
    // email verification, but #1975 makes it the primary session-establishing
    // path for two flows (register-with-skip, then the accept's tenant-scoped
    // re-issue), so a blank name/email would persist for the whole session.
    //
    // Guarded on matching id: identity is never carried across a different
    // user, so a swap to another account still starts clean.
    const prev = loadStoredUser();
    ++sessionGenRef.current; // a session install, like setSession (PR #3275)
    const carry = prev && prev.id === payload.userId ? prev : null;
    const u: User = {
      id:    payload.userId,
      email: payload.email || carry?.email || '',
      name:  payload.name  || carry?.name  || '',
    };
    localStorage.setItem('blueprint_token', jwt);
    localStorage.setItem('blueprint_user', JSON.stringify(u));
    setToken(jwt);
    setUser(u);
  }, [setToken]);

  const logout = useCallback(() => {
    ++sessionGenRef.current;
    localStorage.removeItem('blueprint_token');
    localStorage.removeItem('blueprint_user');
    // Clear partner context keys to prevent stale tokens surviving logout
    localStorage.removeItem('partnerContextToken');
    localStorage.removeItem('partnerOriginalToken');
    localStorage.removeItem('partnerReturnPath');
    localStorage.removeItem('partnerAccountId');
    setToken(null);
    setUser(null);
  }, [setToken]);

  const resetSession = useCallback((expectedToken: string | null) => {
    if (expectedToken === null || tokenRef.current !== expectedToken) return;
    setToken(null);
    setUser(null);
  }, [setToken]);

  // #3269 — when a TENANT_ACCESS_REVOKED refusal cleared stored credentials,
  // the in-memory session must go too, on every page. Otherwise a page that
  // doesn't navigate away (e.g. LoginPage, where the handler skips its
  // /login redirect) keeps a stale token that ProtectedRoute still admits,
  // while every later request goes out with no Bearer token.
  // The event carries the refused token; only the session it names is reset.
  useEffect(() => {
    const onRevoked = (e: Event) => {
      const refused = (e as CustomEvent<{ refusedToken?: unknown }>).detail?.refusedToken;
      if (typeof refused === 'string') resetSession(refused);
    };
    window.addEventListener(TENANT_ACCESS_REVOKED_EVENT, onRevoked);
    return () => window.removeEventListener(TENANT_ACCESS_REVOKED_EVENT, onRevoked);
  }, [resetSession]);

  const value = useMemo<AuthContextValue>(() => ({
    user, token, login, logout, resetSession, register, setSession, registerSme, setAuthFromToken,
  }), [user, token, login, logout, resetSession, register, setSession, registerSme, setAuthFromToken]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/**
 * #2846 — non-throwing sibling of useAuth, same shape as TenantContext's
 * useOptionalTenantContext: returns null outside an AuthProvider instead of
 * throwing. For components mounted inside pages whose existing tests render
 * without an AuthProvider; callers must treat null as "no signed-in user".
 */
export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}
